import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import Modal from '../ui/Modal';
import { API_URL } from '../../config/api';

const getTagStyles = (priority) => {
    switch ((priority || '').toLowerCase()) {
        case 'alta': return { bg: '#fee2e2', text: '#991b1b', label: 'ALTA' };
        case 'media': return { bg: '#ffedd5', text: '#9a3412', label: 'MEDIA' };
        case 'baja': return { bg: '#e0f2fe', text: '#0369a1', label: 'BAJA' };
        default: return { bg: '#f1f5f9', text: '#475569', label: 'NORMAL' };
    }
};

const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '14px',
    outline: 'none',
    fontFamily: 'inherit',
    transition: 'all 0.2s',
    backgroundColor: '#ffffff',
    color: '#0f172a'
};

const labelStyle = {
    display: 'block',
    marginBottom: '6px',
    fontSize: '13px',
    fontWeight: 600,
    color: '#475569'
};

const QualityKanban = ({ tasks = [], onRefresh }) => {
    const [showModal, setShowModal] = useState(false);
    const [editingTask, setEditingTask] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        area: '',
        priority: 'Media',
        due_date: new Date().toISOString().split('T')[0],
        status: 'Pendiente'
    });

    const columns = [
        { id: 'Pendiente', label: 'Pendientes', color: '#94a3b8' },
        { id: 'En Progreso', label: 'En Progreso', color: '#3b82f6' },
        { id: 'Revisión', label: 'Revisión', color: '#f59e0b' },
        { id: 'Completado', label: 'Completados', color: '#10b981' },
        { id: 'Fallos', label: 'Fallos', color: '#ef4444' }
    ];

    const openCreateModal = (columnId) => {
        setEditingTask(null);
        setFormData({
            title: '',
            description: '',
            area: '',
            priority: 'Media',
            due_date: new Date().toISOString().split('T')[0],
            status: columnId
        });
        setShowModal(true);
    };

    const openEditModal = (task) => {
        setEditingTask(task);
        setFormData({
            title: task.title || '',
            description: task.description || '',
            area: task.area || '',
            priority: task.priority || 'Media',
            due_date: task.due_date ? task.due_date.split('T')[0] : new Date().toISOString().split('T')[0],
            status: task.status || 'Pendiente'
        });
        setShowModal(true);
    };

    const handleSubmit = async () => {
        if (!formData.title.trim()) {
            alert('El título es obligatorio');
            return;
        }

        try {
            const url = editingTask
                ? `${API_URL}/api/quality/tasks/${editingTask.id}`
                : `${API_URL}/api/quality/tasks`;

            const method = editingTask ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(formData)
            });

            if (response.ok) {
                setShowModal(false);
                if (onRefresh) onRefresh();
            } else {
                const error = await response.json();
                alert(`Error: ${error.detail || 'No se pudo guardar la tarea'}`);
            }
        } catch (error) {
            console.error('Error saving task:', error);
            alert('Error al guardar la tarea');
        }
    };

    return (
        <>
            <div style={{
                display: 'flex',
                gap: '12px',
                overflowX: 'auto',
                paddingBottom: '16px',
                minHeight: '580px',
                alignItems: 'flex-start'
            }}>
                {columns.map(col => {
                    const colTasks = tasks.filter(t => t.status === col.id);

                    return (
                        <div key={col.id} style={{
                            flex: '1',
                            minWidth: '190px',
                            maxWidth: '220px',
                            background: '#f8fafc',
                            borderRadius: '10px',
                            padding: '12px',
                            display: 'flex',
                            flexDirection: 'column',
                            border: '1px solid #e2e8f0'
                        }}>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '12px',
                                paddingBottom: '10px',
                                borderBottom: `2px solid ${col.color}20`
                            }}>
                                <h3 style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: '#334155' }}>
                                    {col.label}
                                    <span style={{
                                        marginLeft: '6px',
                                        fontSize: '10px',
                                        background: col.color + '20',
                                        color: col.color,
                                        padding: '2px 5px',
                                        borderRadius: '8px',
                                        fontWeight: 700
                                    }}>
                                        {colTasks.length}
                                    </span>
                                </h3>
                                <button
                                    onClick={() => openCreateModal(col.id)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        padding: '3px',
                                        borderRadius: '4px',
                                        transition: 'background 0.2s',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.background = '#e2e8f0'}
                                    onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                                >
                                    <Plus size={14} color="#64748b" />
                                </button>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', minHeight: '50px' }}>
                                {colTasks.length === 0 && (
                                    <div style={{ textAlign: 'center', padding: '16px', color: '#cbd5e1', fontSize: '11px', fontStyle: 'italic' }}>
                                        Sin tareas
                                    </div>
                                )}
                                {colTasks.map(task => {
                                    const tagStyle = getTagStyles(task.priority);
                                    const formattedDate = task.due_date ? new Date(task.due_date).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' }) : '';

                                    return (
                                        <div
                                            key={task.id}
                                            onClick={() => openEditModal(task)}
                                            style={{
                                                background: '#ffffff',
                                                borderRadius: '8px',
                                                padding: '10px',
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                                border: '1px solid #e2e8f0',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.boxShadow = '0 3px 8px rgba(0,0,0,0.1)';
                                                e.currentTarget.style.transform = 'translateY(-1px)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                                                e.currentTarget.style.transform = 'translateY(0)';
                                            }}
                                        >
                                            <h4 style={{ margin: '0 0 6px 0', fontSize: '12px', fontWeight: 600, color: '#0f172a', lineHeight: '1.3' }}>
                                                {task.title}
                                            </h4>
                                            {task.description && (
                                                <p style={{
                                                    margin: '0 0 8px 0',
                                                    fontSize: '11px',
                                                    color: '#64748b',
                                                    lineHeight: '1.4',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden'
                                                }}>
                                                    {task.description}
                                                </p>
                                            )}

                                            {task.area && (
                                                <div style={{
                                                    display: 'inline-block',
                                                    background: '#f1f5f9',
                                                    padding: '3px 6px',
                                                    borderRadius: '5px',
                                                    fontSize: '10px',
                                                    fontWeight: 600,
                                                    color: '#475569',
                                                    marginBottom: '8px',
                                                    maxWidth: '100%',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {task.area}
                                                </div>
                                            )}

                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
                                                <span style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 500 }}>
                                                    {formattedDate || 'Sin fecha'}
                                                </span>
                                                <span style={{
                                                    background: tagStyle.bg,
                                                    color: tagStyle.text,
                                                    padding: '2px 6px',
                                                    borderRadius: '8px',
                                                    fontSize: '9px',
                                                    fontWeight: 700,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.3px'
                                                }}>
                                                    {tagStyle.label}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Modal para crear/editar tarea */}
            <Modal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                title={editingTask ? 'Editar Tarea' : 'Nueva Tarea'}
                maxWidth="600px"
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                    {/* Título */}
                    <div>
                        <label style={labelStyle}>
                            Título <span style={{ color: '#ef4444' }}>*</span>
                        </label>
                        <input
                            type="text"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            style={inputStyle}
                            placeholder="Ej: Revisión de Protocolo A"
                        />
                    </div>

                    {/* Descripción */}
                    <div>
                        <label style={labelStyle}>Descripción</label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            style={{
                                ...inputStyle,
                                minHeight: '90px',
                                resize: 'vertical'
                            }}
                            placeholder="Descripción detallada de la tarea..."
                        />
                    </div>

                    {/* Área y Prioridad */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                        <div>
                            <label style={labelStyle}>Área</label>
                            <input
                                type="text"
                                value={formData.area}
                                onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                                style={inputStyle}
                                placeholder="Ej: Atención al Cliente"
                            />
                        </div>

                        <div>
                            <label style={labelStyle}>Prioridad</label>
                            <select
                                value={formData.priority}
                                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                                style={{
                                    ...inputStyle,
                                    cursor: 'pointer',
                                    appearance: 'none',
                                    backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%23475569\' d=\'M6 9L1 4h10z\'/%3E%3C/svg%3E")',
                                    backgroundRepeat: 'no-repeat',
                                    backgroundPosition: 'right 12px center',
                                    paddingRight: '36px'
                                }}
                            >
                                <option value="Baja">Baja</option>
                                <option value="Media">Media</option>
                                <option value="Alta">Alta</option>
                            </select>
                        </div>
                    </div>

                    {/* Estado y Fecha */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                        <div>
                            <label style={labelStyle}>Estado</label>
                            <select
                                value={formData.status}
                                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                style={{
                                    ...inputStyle,
                                    cursor: 'pointer',
                                    appearance: 'none',
                                    backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%23475569\' d=\'M6 9L1 4h10z\'/%3E%3C/svg%3E")',
                                    backgroundRepeat: 'no-repeat',
                                    backgroundPosition: 'right 12px center',
                                    paddingRight: '36px'
                                }}
                            >
                                <option value="Pendiente">Pendiente</option>
                                <option value="En Progreso">En Progreso</option>
                                <option value="Revisión">Revisión</option>
                                <option value="Completado">Completado</option>
                                <option value="Fallos">Fallos</option>
                            </select>
                        </div>

                        <div>
                            <label style={labelStyle}>Fecha de Vencimiento</label>
                            <input
                                type="date"
                                value={formData.due_date}
                                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                                style={inputStyle}
                            />
                        </div>
                    </div>

                    {/* Botones */}
                    <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
                        <button
                            onClick={() => setShowModal(false)}
                            style={{
                                flex: 1,
                                padding: '12px',
                                borderRadius: '8px',
                                border: '1px solid #e2e8f0',
                                background: '#ffffff',
                                color: '#64748b',
                                fontSize: '14px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                fontFamily: 'inherit'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#f8fafc';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = '#ffffff';
                            }}
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={handleSubmit}
                            style={{
                                flex: 1,
                                padding: '12px',
                                borderRadius: '8px',
                                border: 'none',
                                background: '#0c2c6c',
                                color: '#ffffff',
                                fontSize: '14px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                fontFamily: 'inherit'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#1e40af';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = '#0c2c6c';
                            }}
                        >
                            {editingTask ? 'Guardar Cambios' : 'Crear Tarea'}
                        </button>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default QualityKanban;
